import React, { useState, useEffect } from 'react'
import Swal from 'sweetalert2'
import apiClient from '../../config/api'

const ProductCatalog = () => {
  const [products, setProducts] = useState([])
  const [filteredProducts, setFilteredProducts] = useState([])
  const [loading, setLoading] = useState(true)
  const [filters, setFilters] = useState({
    search: '',
    category: '',
    status: ''
  })

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true)
        const productsResponse = await apiClient.get('/api/products/products/')
        
        setProducts(productsResponse.data)
        setFilteredProducts(productsResponse.data)
      } catch (error) {
        console.error('Error fetching data:', error)
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to fetch data. Please try again later.'
        })
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [])

  useEffect(() => {
    let filtered = [...products]

    if (filters.search) {
      const searchLower = filters.search.toLowerCase()
      filtered = filtered.filter(product =>
        product.name?.toLowerCase().includes(searchLower) ||
        product.sku?.toLowerCase().includes(searchLower) ||
        product.category_name?.toLowerCase().includes(searchLower)
      )
    }

    if (filters.category) {
      filtered = filtered.filter(product => product.category_name === filters.category)
    }

    if (filters.status) {
      const statusFilter = filters.status === 'active'
      filtered = filtered.filter(product => product.is_active === statusFilter)
    }

    setFilteredProducts(filtered)
  }, [products, filters])

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A'
    const date = new Date(dateString)
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  const handleRowClick = (product, e) => {
    const htmlContent = `
      <div style="text-align: left; font-size: 14px;">
        <p><strong>ID:</strong> ${product.id}</p>
        <p><strong>SKU:</strong> ${product.sku}</p>
        <p><strong>Name:</strong> ${product.name}</p>
        <p><strong>Category:</strong> ${product.category_name} (ID: ${product.category})</p>
        <p><strong>Unit:</strong> ${product.unit_name} (ID: ${product.unit})</p>
        <p><strong>Status:</strong> <span style="color: ${product.is_active ? '#28a745' : '#dc3545'}">${product.is_active ? 'Active' : 'Inactive'}</span></p>
        <p><strong>Created:</strong> ${formatDate(product.created_at)}</p>
        <p><strong>Updated:</strong> ${formatDate(product.updated_at)}</p>
      </div>
    `
    
    Swal.fire({
      title: 'Product Details',
      html: htmlContent,
      icon: 'info',
      width: '500px',
      confirmButtonText: 'Close',
      confirmButtonColor: '#3085d6'
    })
  }

  const totalProducts = filteredProducts.length
  const activeProducts = filteredProducts.filter(p => p.is_active).length
  const inactiveProducts = filteredProducts.filter(p => !p.is_active).length

  return (
    <>
      <div className="pagetitle">
        <h1>Product Catalog</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item"><a href="/">Home</a></li>
            <li className="breadcrumb-item">Products</li>
            <li className="breadcrumb-item active">Product Catalog</li>
          </ol>
        </nav>
      </div>

      <section className="section">
        <div className="row">
          <div className="col-lg-12">
            <div className="card">
              <div className="card-body">
                {/* Header with Stats */}
                <div className="d-flex justify-content-between align-items-center flex-wrap mb-3" style={{ 
                  paddingBottom: '15px',
                  borderBottom: '2px solid #e9ecef'
                }}>
                  <div className="d-flex align-items-center gap-4 flex-wrap">
                    <h5 className="card-title mb-0">
                      <i className="bi bi-box-seam me-2"></i>
                      Product Catalog
                    </h5>
                    
                    {/* Stats Badges */}
                    <div className="d-flex align-items-center gap-3 flex-wrap">
                      <div className="d-flex align-items-center" style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                      }}>
                        <i className="bi bi-box-seam me-2"></i>
                        <span>Total: <strong>{totalProducts}</strong></span>
                      </div>
                      
                      <div className="d-flex align-items-center" style={{
                        background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(17, 153, 142, 0.3)'
                      }}>
                        <i className="bi bi-check-circle me-2"></i>
                        <span>Active: <strong>{activeProducts}</strong></span>
                      </div>
                      
                      <div className="d-flex align-items-center" style={{
                        background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                        padding: '6px 14px',
                        borderRadius: '20px',
                        color: 'white',
                        fontSize: '13px',
                        fontWeight: '600',
                        boxShadow: '0 2px 8px rgba(245, 87, 108, 0.3)'
                      }}>
                        <i className="bi bi-x-circle me-2"></i>
                        <span>Inactive: <strong>{inactiveProducts}</strong></span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Filters */}
                <div className="row mb-3 align-items-center">
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search products..."
                      value={filters.search}
                      onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Category"
                      value={filters.category}
                      onChange={(e) => setFilters({ ...filters, category: e.target.value })}
                    />
                  </div>
                  <div className="col-md-4">
                    <select
                      className="form-select"
                      value={filters.status}
                      onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                    >
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>

                {/* Products Table */}
                {loading ? (
                  <p>Loading...</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-striped">
                      <thead>
                        <tr>
                          <th>Name</th>
                          <th>SKU</th>
                          <th>Category</th>
                          <th>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredProducts.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="text-center">No products found</td>
                          </tr>
                        ) : (
                          filteredProducts.map(product => (
                            <tr 
                              key={product.id}
                              onClick={(e) => handleRowClick(product, e)}
                              style={{ cursor: 'pointer' }}
                            >
                              <td>{product.name}</td>
                              <td>{product.sku}</td>
                              <td>{product.category_name}</td>
                              <td>
                                <span className={`badge ${product.is_active ? 'bg-success' : 'bg-danger'}`}>
                                  {product.is_active ? 'Active' : 'Inactive'}
                                </span>
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}

export default ProductCatalog






